import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/Button';
import { TextField } from '../components/ui/Field';
import { LanguageSwitcher } from '../components/layout/LanguageSwitcher';
import { ApiError, profile } from '../lib/api';
import { track } from '../lib/analytics';
import { apiErrorMessage } from '../lib/errors';

/**
 * Creator onboarding (F-054, Wingspan cut 11) — the guided first-run a new
 * creator walks: welcome (the F-047 sign-up credits), pick a handle + public
 * name (PATCH /v1/me/profile), then a clear CTA into the first upload. Fully
 * skippable and non-blocking — every step offers "skip for now", and nothing
 * traps a user who would rather browse. FINAL_STATE §2 creator rooms.
 */

/** The sign-up credit grant (F-047 / D-010 §2). */
const SIGNUP_CREDITS = 10;
const ONBOARDED_KEY = 'xico.onboarded';

type Step = 'welcome' | 'profile' | 'upload';

export function Onboarding() {
  const { t } = useI18n();
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const finishTarget = useMemo(
    () => (location.state as { from?: string } | null)?.from ?? '/studio',
    [location.state],
  );

  const [step, setStep] = useState<Step>('welcome');
  const [handle, setHandle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [handleError, setHandleError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Anonymous users have nothing to onboard — send them to sign in.
  useEffect(() => {
    if (status === 'anonymous') navigate('/auth', { replace: true });
  }, [status, navigate]);

  useEffect(() => {
    track('onboarding_started', {});
  }, []);

  useEffect(() => {
    if (user?.name && displayName === '') setDisplayName(user.name);
  }, [user, displayName]);

  function markOnboarded() {
    try {
      localStorage.setItem(ONBOARDED_KEY, '1');
    } catch {
      // private mode / storage disabled — non-fatal, onboarding still works.
    }
  }

  function skip() {
    markOnboarded();
    navigate('/market', { replace: true });
  }

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    setHandleError(null);
    setFormError(null);
    setSaving(true);
    try {
      await profile.update({
        handle: handle.trim().toLowerCase(),
        display_name: displayName.trim() || undefined,
      });
      track('onboarding_completed', {});
      markOnboarded();
      setStep('upload');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setHandleError(t.onboarding.handleTaken);
      } else if (err instanceof ApiError && err.status === 400) {
        setHandleError(t.onboarding.handleInvalid);
      } else {
        setFormError(apiErrorMessage(err, t));
      }
    } finally {
      setSaving(false);
    }
  }

  function goToStudio() {
    markOnboarded();
    navigate(finishTarget, { replace: true });
  }

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <header className="mx-auto flex w-full max-w-content items-center justify-between px-4 py-4 sm:px-6">
        <Link to="/" className="type-heading-2 text-primary focus-ring rounded-sm">
          {t.common.brand}
        </Link>
        <LanguageSwitcher />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {step === 'welcome' ? (
            <section aria-labelledby="ob-welcome">
              <p className="type-caption text-muted">{t.onboarding.stepOf(1, 3)}</p>
              <h1 id="ob-welcome" className="type-display-lg text-primary mt-1">
                {t.onboarding.welcomeTitle}
              </h1>
              <p className="type-body text-secondary mt-2">{t.onboarding.welcomeBody(SIGNUP_CREDITS)}</p>
              <div className="mt-8 flex flex-col gap-3">
                <Button size="lg" block onClick={() => setStep('profile')}>
                  {t.onboarding.next}
                </Button>
                <button
                  type="button"
                  className="type-label text-secondary hover:text-primary focus-ring rounded-sm"
                  onClick={skip}
                >
                  {t.onboarding.skip}
                </button>
              </div>
            </section>
          ) : null}

          {step === 'profile' ? (
            <section aria-labelledby="ob-profile">
              <p className="type-caption text-muted">{t.onboarding.stepOf(2, 3)}</p>
              <h1 id="ob-profile" className="type-display-lg text-primary mt-1">
                {t.onboarding.handleStepTitle}
              </h1>
              <p className="type-body text-secondary mt-2">{t.onboarding.handleStepBody}</p>

              <form className="mt-8 flex flex-col gap-4" onSubmit={saveProfile} noValidate>
                <TextField
                  label={t.onboarding.handleLabel}
                  placeholder={t.onboarding.handlePlaceholder}
                  help={t.onboarding.handleHelp}
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  error={handleError}
                  required
                />
                <TextField
                  label={t.onboarding.displayNameLabel}
                  placeholder={t.onboarding.displayNamePlaceholder}
                  autoComplete="off"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />

                {formError ? (
                  <p className="type-body-sm text-danger" role="alert">
                    {formError}
                  </p>
                ) : null}

                <Button type="submit" size="lg" block loading={saving} disabled={handle.trim().length === 0}>
                  {saving ? t.onboarding.saving : t.onboarding.next}
                </Button>
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    className="type-label text-secondary hover:text-primary focus-ring rounded-sm"
                    onClick={() => setStep('welcome')}
                  >
                    {t.onboarding.back}
                  </button>
                  <button
                    type="button"
                    className="type-label text-secondary hover:text-primary focus-ring rounded-sm"
                    onClick={skip}
                  >
                    {t.onboarding.skip}
                  </button>
                </div>
              </form>
            </section>
          ) : null}

          {step === 'upload' ? (
            <section aria-labelledby="ob-upload">
              <p className="type-caption text-muted">{t.onboarding.stepOf(3, 3)}</p>
              <h1 id="ob-upload" className="type-display-lg text-primary mt-1">
                {t.onboarding.uploadStepTitle}
              </h1>
              <p className="type-body text-secondary mt-2">{t.onboarding.uploadStepBody}</p>
              <div className="mt-8 flex flex-col gap-3">
                <Button size="lg" block onClick={goToStudio}>
                  {t.onboarding.goToStudio}
                </Button>
                <button
                  type="button"
                  className="type-label text-secondary hover:text-primary focus-ring rounded-sm"
                  onClick={skip}
                >
                  {t.onboarding.finish}
                </button>
              </div>
            </section>
          ) : null}
        </div>
      </main>
    </div>
  );
}
